import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import RideMap from './RideMap';
import { getDriverProfile } from '../services/api';

// Browser Intl data for month names is incomplete for some locales (e.g. hy)
// and silently falls back to the OS language instead of the selected app
// language - so month abbreviations are hardcoded here instead of relying
// on toLocaleString's { month: 'short' } for this one piece.
const MONTH_ABBR = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  ru: ['янв.', 'февр.', 'март', 'апр.', 'май', 'июнь', 'июль', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.'],
  hy: ['հնվ', 'փտվ', 'մրտ', 'ապր', 'մյս', 'հնս', 'հլս', 'օգս', 'սեպ', 'հոկ', 'նոյ', 'դեկ'],
  fr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'],
  de: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

function formatRideDate(isoString, lang) {
  const d = new Date(isoString);
  const month = (MONTH_ABBR[lang] || MONTH_ABBR.en)[d.getMonth()];
  const day = d.toLocaleString(lang, { day: 'numeric' });
  const time = d.toLocaleString(lang, { hour: '2-digit', minute: '2-digit' });
  return `${month} ${day}, ${time}`;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function StarRating({ value, onRate, readonly }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onRate && onRate(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`text-xl leading-none transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} ${
            star <= (hover || value) ? 'text-amber-400' : 'text-slate-200'
          }`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

const COMPLIMENT_TYPES = ['GREAT_CONVERSATION', 'CLEAN_CAR', 'SAFE_DRIVING', 'GREAT_MUSIC', 'ON_TIME'];

export default function RideCard({ ride, showActions, onAccept, onArrive, onStart, onComplete, onCancel, onRate, onRateClient, onSendCompliments }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [draftRating, setDraftRating] = useState(0);
  const [draftReview, setDraftReview] = useState('');
  const [driverDraftRating, setDriverDraftRating] = useState(0);
  const [driverDraftReview, setDriverDraftReview] = useState('');
  const [selectedCompliments, setSelectedCompliments] = useState([]);
  const [complimentsSent, setComplimentsSent] = useState(false);
  const [driverProfile, setDriverProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const { t, lang } = useLang();
  const rc = t.rideCard;

  const handleOpenDriverProfile = async () => {
    if (!ride.assignedDriver) return;
    setProfileLoading(true);
    try {
      const res = await getDriverProfile(ride.assignedDriver.id);
      setDriverProfile(res.data);
    } catch {
      // ignore - profile just won't open
    } finally {
      setProfileLoading(false);
    }
  };

  const toggleCompliment = (code) => {
    setSelectedCompliments((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  const STATUS = {
    NEW:         { dot: 'bg-blue-400',    badge: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',     border: 'border-l-blue-500'    },
    ACCEPTED:    { dot: 'bg-amber-400',   badge: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',  border: 'border-l-amber-400'   },
    ARRIVED:     { dot: 'bg-orange-400',  badge: 'bg-orange-500/20 text-orange-400 border border-orange-500/30', border: 'border-l-orange-400' },
    IN_PROGRESS: { dot: 'bg-sky-400',     badge: 'bg-sky-500/20 text-sky-400 border border-sky-500/30',        border: 'border-l-sky-400'     },
    COMPLETED:   { dot: 'bg-green-400',   badge: 'bg-green-500/20 text-green-400 border border-green-500/30',  border: 'border-l-green-500'   },
    CANCELLED:   { dot: 'bg-white/30',    badge: 'bg-white/10 text-white/40 border border-white/20',           border: 'border-l-white/20'    },
  };

  const s = STATUS[ride.status] || { dot: 'bg-white/30', badge: 'bg-white/10 text-white/40 border border-white/20' };
  const label = rc.status[ride.status] || ride.status;

  let etaLabel = null;
  if (ride.status === 'ARRIVED') {
    etaLabel = rc.etaArrived;
  } else if ((ride.status === 'ACCEPTED' || ride.status === 'IN_PROGRESS') && ride.driverLat != null && ride.driverLng != null) {
    const target = ride.status === 'ACCEPTED'
      ? (ride.fromLat != null && ride.fromLng != null ? [ride.fromLat, ride.fromLng] : null)
      : (ride.toLat != null && ride.toLng != null ? [ride.toLat, ride.toLng] : null);
    if (target) {
      const min = Math.max(1, Math.ceil(haversineKm(ride.driverLat, ride.driverLng, target[0], target[1]) / 30 * 60));
      const template = ride.status === 'IN_PROGRESS' ? (rc.etaDestination || rc.eta) : (rc.eta || '~{min} min');
      etaLabel = template.replace('{min}', min);
    }
  }

  return (
    <div className={`bg-white/10 backdrop-blur-sm border border-white/20 border-l-4 ${s.border} rounded-2xl p-6`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white/40 uppercase tracking-wide">{rc.rideNum}{ride.id}</span>
            {ride.rideClass && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white/50">
                {ride.rideClass.charAt(0) + ride.rideClass.slice(1).toLowerCase()}
              </span>
            )}
          </div>
          {ride.createdAt && (
            <p className="text-xs text-white/30 mt-0.5">
              {formatRideDate(ride.createdAt, lang)}
            </p>
          )}
        </div>
        <span className={`inline-flex items-center gap-2 text-sm font-bold px-3.5 py-1.5 rounded-full ${s.badge}`}>
          <span className={`w-2 h-2 rounded-full ${s.dot}`} />
          {label}
        </span>
      </div>

      <div className="bg-white/10 rounded-xl p-4 mb-4 space-y-2 border border-white/10">
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-green-400 shrink-0" />
          <span className="text-sm font-medium text-white truncate">{ride.fromLocation}</span>
        </div>
        <div className="ml-[5px] border-l-2 border-dashed border-white/20 h-3" />
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400 shrink-0" />
          <span className="text-sm font-medium text-white truncate">{ride.toLocation}</span>
        </div>
      </div>

      {ride.fromLat && ride.toLat && ['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(ride.status) && (
        <div className="mb-4">
          {etaLabel && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-xl leading-none">🚗</span>
              <span className="text-sm font-semibold text-amber-400">{etaLabel}</span>
            </div>
          )}
          <RideMap
            fromCoords={[ride.fromLat, ride.fromLng]}
            toCoords={[ride.toLat, ride.toLng]}
            fromLabel={ride.fromLocation}
            toLabel={ride.toLocation}
            driverCoords={ride.driverLat && ride.driverLng ? [ride.driverLat, ride.driverLng] : null}
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-2xl font-black text-primary-400">€{ride.price != null ? parseFloat(ride.price).toFixed(2) : '—'}</span>
        {ride.assignedDriver && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleOpenDriverProfile} disabled={profileLoading} className="flex items-center gap-2 text-left">
              <div className="w-7 h-7 rounded-full bg-primary-600/30 flex items-center justify-center text-xs font-bold text-primary-400">{ride.assignedDriver.name?.[0]}</div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-white/70 hover:text-white transition-colors">{ride.assignedDriver.name}</span>
                  {ride.assignedDriver.averageRating && (
                    <span className="text-xs text-amber-400 font-semibold">★ {ride.assignedDriver.averageRating.toFixed(1)}</span>
                  )}
                </div>
              </div>
            </button>
            {['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(ride.status) && ride.assignedDriver.phone && (
              <a
                href={`tel:${ride.assignedDriver.phone}`}
                title={rc.driverPhone}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-600/20 border border-primary-500/30 text-primary-400 hover:bg-primary-600/40 transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-.464 1.465l-.657.627a9.942 9.942 0 004.804 4.804l.627-.657a1.5 1.5 0 011.465-.464l3.223.716A1.5 1.5 0 0117 14.352V15.5A1.5 1.5 0 0115.5 17h-1C7.611 17 3 12.389 3 6.5v-1A1.5 1.5 0 012 3.5z" /></svg>
              </a>
            )}
          </div>
        )}
        {!ride.assignedDriver && ride.client && (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white/70">{ride.client.name?.[0]}</div>
            <div>
              <span className="text-sm text-white/60">{ride.client.name}</span>
            </div>
            {['ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(ride.status) && ride.client.phone && (
              <a
                href={`tel:${ride.client.phone}`}
                title={rc.clientPhone}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-600/20 border border-primary-500/30 text-primary-400 hover:bg-primary-600/40 transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-.464 1.465l-.657.627a9.942 9.942 0 004.804 4.804l.627-.657a1.5 1.5 0 011.465-.464l3.223.716A1.5 1.5 0 0117 14.352V15.5A1.5 1.5 0 0115.5 17h-1C7.611 17 3 12.389 3 6.5v-1A1.5 1.5 0 012 3.5z" /></svg>
              </a>
            )}
          </div>
        )}
      </div>

      {ride.status === 'COMPLETED' && (
        <div className="mt-3 pt-3 border-t border-white/10">
          {ride.rating ? (
            <div>
              <div className="flex items-center gap-2">
                <StarRating value={ride.rating} readonly />
                <span className="text-xs text-white/40">{rc.yourRating}</span>
              </div>
              {ride.review && (
                <p className="text-sm text-white/60 mt-2 italic">"{ride.review}"</p>
              )}
            </div>
          ) : onRate ? (
            <div>
              <p className="text-xs text-white/40 mb-1.5">{rc.rateRide}</p>
              <StarRating value={draftRating} onRate={setDraftRating} />
              {draftRating > 0 && (
                <>
                  <textarea
                    value={draftReview}
                    onChange={(e) => setDraftReview(e.target.value)}
                    placeholder={rc.reviewPlaceholder}
                    maxLength={500}
                    rows={2}
                    className="w-full mt-2 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                  />
                  <button
                    onClick={() => onRate(ride.id, draftRating, draftReview)}
                    className="mt-2 text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                  >
                    {rc.submitRating}
                  </button>
                </>
              )}
            </div>
          ) : null}

          {onSendCompliments && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-xs text-white/40 mb-1.5">{rc.complimentsTitle}</p>
              <div className="flex flex-wrap gap-2">
                {COMPLIMENT_TYPES.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggleCompliment(code)}
                    disabled={complimentsSent}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors disabled:opacity-50 ${
                      selectedCompliments.includes(code)
                        ? 'bg-primary-600/30 border-primary-500/50 text-primary-300'
                        : 'border-white/20 text-white/50 hover:border-white/40'
                    }`}
                  >
                    {rc.compliments?.[code] || code}
                  </button>
                ))}
              </div>
              {selectedCompliments.length > 0 && !complimentsSent && (
                <button
                  onClick={() => { onSendCompliments(ride.id, selectedCompliments); setComplimentsSent(true); }}
                  className="mt-2 text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                >
                  {rc.sendCompliments}
                </button>
              )}
              {complimentsSent && (
                <p className="text-xs text-green-400 mt-2">{rc.complimentsSent}</p>
              )}
            </div>
          )}

          {ride.driverRating ? (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2">
                <StarRating value={ride.driverRating} readonly />
                <span className="text-xs text-white/40">{rc.yourRatingOfPassenger}</span>
              </div>
              {ride.driverReview && (
                <p className="text-sm text-white/60 mt-2 italic">"{ride.driverReview}"</p>
              )}
            </div>
          ) : onRateClient ? (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-xs text-white/40 mb-1.5">{rc.ratePassenger}</p>
              <StarRating value={driverDraftRating} onRate={setDriverDraftRating} />
              {driverDraftRating > 0 && (
                <>
                  <textarea
                    value={driverDraftReview}
                    onChange={(e) => setDriverDraftReview(e.target.value)}
                    placeholder={rc.reviewPlaceholder}
                    maxLength={500}
                    rows={2}
                    className="w-full mt-2 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                  />
                  <button
                    onClick={() => onRateClient(ride.id, driverDraftRating, driverDraftReview)}
                    className="mt-2 text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                  >
                    {rc.submitRating}
                  </button>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}

      {showActions && ride.status === 'NEW' && onAccept && (
        <button onClick={() => onAccept(ride.id)} className="w-full mt-4 bg-blue-500/20 hover:bg-blue-500/40 border border-blue-500/40 text-blue-400 font-semibold py-3 rounded-full transition-all active:scale-95">{rc.accept}</button>
      )}
      {showActions && ride.status === 'ACCEPTED' && onArrive && (
        <button onClick={() => onArrive(ride.id, ride.version)} className="w-full mt-4 bg-orange-500/20 hover:bg-orange-500/40 border border-orange-500/40 text-orange-400 font-semibold py-3 rounded-full transition-all active:scale-95">{rc.arrive}</button>
      )}
      {showActions && ride.status === 'ARRIVED' && onStart && (
        <button onClick={() => onStart(ride.id, ride.version)} className="w-full mt-4 bg-sky-500/20 hover:bg-sky-500/40 border border-sky-500/40 text-sky-400 font-semibold py-3 rounded-full transition-all active:scale-95">{rc.start}</button>
      )}
      {showActions && ride.status === 'IN_PROGRESS' && onComplete && (
        <button onClick={() => onComplete(ride.id, ride.version)} className="w-full mt-4 bg-green-500/20 hover:bg-green-500/40 border border-green-500/40 text-green-400 font-semibold py-3 rounded-full transition-all active:scale-95">{rc.complete}</button>
      )}

      {showActions && ['NEW', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(ride.status) && onCancel && (
        confirmCancel ? (
          <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
            <p className="text-sm font-semibold text-red-400 mb-3">{rc.cancelConfirmText || 'Are you sure you want to cancel this ride?'}</p>
            <div className="flex gap-2">
              <button
                onClick={() => onCancel(ride.id)}
                className="flex-1 text-sm font-semibold py-2.5 rounded-full bg-red-500/30 hover:bg-red-500/50 border border-red-500/40 text-red-400 transition-all"
              >
                {rc.cancelConfirm || 'Yes, cancel'}
              </button>
              <button
                onClick={() => setConfirmCancel(false)}
                className="flex-1 text-sm font-semibold py-2.5 rounded-full border border-white/20 text-white/50 hover:border-white/40 hover:text-white transition-all"
              >
                {rc.cancelDeny || 'No, keep it'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmCancel(true)}
            className="w-full mt-2 text-sm font-semibold py-2.5 rounded-full border border-white/20 text-white/40 hover:border-red-500/40 hover:text-red-400 transition-all"
          >
            {rc.cancel}
          </button>
        )
      )}

      {driverProfile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDriverProfile(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">{driverProfile.name}</h3>
              <button onClick={() => setDriverProfile(null)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
              {driverProfile.averageRating != null && (
                <span className="text-amber-500 font-semibold">★ {driverProfile.averageRating.toFixed(1)}</span>
              )}
              <span>{rc.completedRides}: {driverProfile.completedRides}</span>
            </div>
            {driverProfile.vehicle && (
              <p className="text-sm text-slate-500 mb-3">
                {[driverProfile.vehicle.model, driverProfile.vehicle.color, driverProfile.vehicle.plateNumber].filter(Boolean).join(' · ')}
              </p>
            )}
            {driverProfile.compliments?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {driverProfile.compliments.map((c) => (
                  <span key={c.code} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary-50 text-primary-600">
                    {rc.compliments?.[c.code] || c.code} · {c.count}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
